import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { TruncatedText } from 'design-system/src/components/TruncatedText/TruncatedText';

const CellLineBreak = styled.div`
  height: 8px;
`;

const CellTextBlock = styled(Typography)`
  margin: 0;
`;

const FormattedCellWrapper = styled.div<{ $width?: number }>`
  ${({ $width }) => ($width ? `width: ${$width}px;` : '')};
`;

export type FormattedCellProps = {
  /**
   * Class name for additional styling; used to pass down styles when using inheritance in styled components
   */
  className?: string;
  /**
   * Unique identifier for the cell content
   */
  id: string;
  /**
   * Main title text to display
   */
  title: string;
  /**
   * Optional subtitle text to display below the title
   */
  subtitle?: string;
  /**
   * Optional body text to display below the subtitle, supports line breaks with \\n
   */
  body?: string;
  /**
   * Whether to truncate the body text
   */
  truncateBody?: boolean;
  /**
   * Custom body component to display below the subtitle (for when you need more control over the body styling)
   */
  customBody?: React.ReactNode;
  /**
   * Optional size for the cell
   */
  size?: number;
};

/**
 * A formatted cell component for displaying structured content in table cells.
 * Supports title, subtitle, and body text with automatic line break handling.
 */
export const FormattedCell = ({
  className,
  id,
  title,
  subtitle,
  body,
  truncateBody,
  customBody,
  size,
}: FormattedCellProps) => {
  return (
    <FormattedCellWrapper className={className} $width={size}>
      <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
        {title}
      </Typography>
      {subtitle && <Typography styledAs="bodyExtraSmallDMSans">{subtitle}</Typography>}
      {body &&
        !truncateBody &&
        // normally we wouldn't use index as a key, but in this case it's necessary because the id will be the same for
        // each line break, so we need to use something that's different for each line break. also, we're not concerned
        // about the lines changing between rerenders
        body.split('\\n').map((line, index) => {
          if (line == '') {
            return <CellLineBreak key={`line-break-${id}-${index}`} />;
          }
          return (
            <CellTextBlock styledAs="bodyExtraSmallDMSans" color="var(--dark-gray)" key={`text-block-${id}-${index}`}>
              {line}
            </CellTextBlock>
          );
        })}
      {body && truncateBody && (
        <Typography styledAs="bodyExtraSmallDMSans" color="var(--dark-gray)" renderedAs="div">
          <TruncatedText lines={1}>{body}</TruncatedText>
        </Typography>
      )}
      {customBody}
    </FormattedCellWrapper>
  );
};

export default FormattedCell;
